// ⚠️  GENERATED — do not edit. Run `npm run inferrex:generate` to regenerate.

// Read import.meta.env via a local cast rather than augmenting the global
// ImportMeta interface — that augmentation would conflict with vite/client's
// ImportMetaEnv declaration in any host app that pulls in both (TS2717).
const __env: Record<string, string | undefined> =
  (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
/**
 * Inferrex JWT Authentication provider (Sovereign / Enterprise path).
 *
 * Active when VITE_AUTH_PROVIDER=inferrex.
 * - Posts credentials to /v1/auth/login on the licensing service
 * - Handles MFA challenge (mfaRequired → verifyMfa)
 * - Stores access token in memory; refresh token in httpOnly cookie (server-set)
 * - Auto-refreshes via /v1/auth/refresh ~30s before expiry
 *
 * Zero external IdP — talks only to the licensing service inside the cluster.
 */

import {
  createContext, useCallback, useContext, useEffect,
  useMemo, useRef, useState,
} from 'react';
import type {
  AuthContextValue, AuthProviderProps, AuthState, AuthUser, SocialProvider,
} from './types.js';

const InferrexAuthContext = createContext<AuthContextValue | null>(null);

// VITE_API_BASE_URL is the api service root (e.g. http://api.inferrex.local:30080).
// Auth endpoints live at /v1/auth/{login,refresh,...} on that host.
const API_BASE = (__env['VITE_API_BASE_URL'] as string | undefined) ?? '';
const AUTH_BASE = `${API_BASE.replace(/\/+$/, '')}/v1/auth`;
const REFRESH_BUFFER_MS = 30 * 1000; // refresh 30s before expiry

interface LoginResponse {
  mfaRequired: boolean;
  mfaToken?: string;
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
}

interface JwtClaims {
  customer_id: string;
  user_id: string;
  email: string;
  roles: string[];
}

function decodeClaims(token: string): JwtClaims | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3 || !parts[1]) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return {
      customer_id: payload.customer_id,
      user_id: payload.user_id,
      email: payload.email,
      roles: payload.roles ?? [],
    };
  } catch {
    return null;
  }
}

function claimsToUser(claims: JwtClaims): AuthUser {
  return {
    uid: claims.user_id,
    email: claims.email,
    displayName: claims.email.split('@')[0] ?? null,
    roles: claims.roles,
    customerId: claims.customer_id,
  };
}

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: res.statusText } }));
    throw new Error(err?.error?.message ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export function InferrexAuthProvider({ children }: AuthProviderProps) {
  const refreshTokenRef = useRef<string | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mfaTokenRef = useRef<string | null>(null);

  const [state, setState] = useState<AuthState>({
    user: null, loading: false, token: null, configError: null, mfaPending: false, isAdmin: false,
  });

  const scheduleRefresh = useCallback((expiresInSeconds: number) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const delay = Math.max(0, expiresInSeconds * 1000 - REFRESH_BUFFER_MS);
    refreshTimerRef.current = setTimeout(() => { void doRefresh(); }, delay);
  }, []);

  const applyTokens = useCallback((accessToken: string, refreshToken: string, expiresIn: number) => {
    refreshTokenRef.current = refreshToken;
    const claims = decodeClaims(accessToken);
    const isAdmin = (claims?.roles ?? []).some((r) => r === 'admin' || r === 'owner');
    setState({
      user: claims ? claimsToUser(claims) : null,
      loading: false, token: accessToken, configError: null, mfaPending: false, isAdmin,
    });
    scheduleRefresh(expiresIn);
  }, [scheduleRefresh]);

  const doRefresh = useCallback(async () => {
    const rt = refreshTokenRef.current;
    if (!rt) return;
    try {
      const result = await postJson<{ accessToken: string; refreshToken: string; expiresIn: number }>(
        `${AUTH_BASE}/refresh`,
        { refreshToken: rt },
      );
      applyTokens(result.accessToken, result.refreshToken, result.expiresIn);
    } catch {
      // Refresh failed — clear session
      refreshTokenRef.current = null;
      setState({ user: null, loading: false, token: null, configError: null, mfaPending: false, isAdmin: false });
    }
  }, [applyTokens]);

  useEffect(() => {
    return () => { if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current); };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setState((s) => ({ ...s, loading: true, configError: null }));
    try {
      const result = await postJson<LoginResponse>(`${AUTH_BASE}/login`, { email, password });
      if (result.mfaRequired && result.mfaToken) {
        mfaTokenRef.current = result.mfaToken;
        setState({ user: null, loading: false, token: null, configError: null, mfaPending: true, isAdmin: false });
        return;
      }
      if (!result.accessToken || !result.refreshToken || !result.expiresIn) {
        throw new Error('Login response missing tokens');
      }
      applyTokens(result.accessToken, result.refreshToken, result.expiresIn);
    } catch (err) {
      setState({
        user: null, loading: false, token: null, mfaPending: false, isAdmin: false,
        configError: err instanceof Error ? err.message : 'Login failed',
      });
      throw err;
    }
  }, [applyTokens]);

  const verifyMfa = useCallback(async (code: string) => {
    const mfaToken = mfaTokenRef.current;
    if (!mfaToken) throw new Error('No MFA challenge in progress');
    setState((s) => ({ ...s, loading: true, configError: null }));
    try {
      const result = await postJson<{ accessToken: string; refreshToken: string; expiresIn: number }>(
        `${AUTH_BASE}/mfa/verify`,
        { mfaToken, code },
      );
      mfaTokenRef.current = null;
      applyTokens(result.accessToken, result.refreshToken, result.expiresIn);
    } catch (err) {
      setState((s) => ({
        ...s, loading: false,
        configError: err instanceof Error ? err.message : 'MFA verification failed',
      }));
      throw err;
    }
  }, [applyTokens]);

  const signUp = useCallback(async (_email: string, _password: string) => {
    throw new Error('Self-service signup not available in sovereign mode. Contact your administrator.');
  }, []);

  const signInWithSocial = useCallback(async (provider: SocialProvider) => {
    // Sovereign SSO flow: POST /v1/auth/sso/redirect to get the OIDC authorize
    // URL for the configured provider, then redirect the browser there. The
    // provider completes auth + redirects back to /v1/auth/sso/callback,
    // which mints an Inferrex JWT (no Firebase, no external SaaS).
    try {
      const res = await fetch(`${AUTH_BASE}/sso/redirect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ providerId: provider, returnTo: window.location.href }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
        throw new Error(body.message ?? body.error ?? `SSO provider not configured: ${provider}`);
      }
      const data = (await res.json()) as { redirectUrl?: string };
      if (!data.redirectUrl) {
        throw new Error('SSO provider returned no redirect URL — check provider configuration.');
      }
      window.location.assign(data.redirectUrl);
    } catch (err) {
      throw err instanceof Error
        ? err
        : new Error('SSO sign-in failed. Contact your administrator if this persists.');
    }
  }, []);

  const signOut = useCallback(async () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTokenRef.current = null;
    mfaTokenRef.current = null;
    setState({ user: null, loading: false, token: null, configError: null, mfaPending: false, isAdmin: false });
    // Best-effort server revoke (no error if it fails — session expires anyway)
    try { await fetch(`${AUTH_BASE}/logout`, { method: 'POST', credentials: 'include' }); } catch { /* noop */ }
  }, []);

  const resetPassword = useCallback(async (_email: string) => {
    throw new Error('Password reset is admin-managed in sovereign mode. Contact your administrator.');
  }, []);

  const getToken = useCallback(async (): Promise<string | null> => state.token, [state.token]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signIn, signUp, signInWithSocial, signOut, resetPassword, getToken, verifyMfa }),
    [state, signIn, signUp, signInWithSocial, signOut, resetPassword, getToken, verifyMfa],
  );

  return <InferrexAuthContext.Provider value={value}>{children}</InferrexAuthContext.Provider>;
}

export function useInferrexAuth(): AuthContextValue {
  const ctx = useContext(InferrexAuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
